import { forwardRef, useId } from 'react';

/**
 * Native `<select>` wrapped in the field label/hint/error contract
 * (spec §7.1, §12). Deliberately native rather than a custom listbox: it gets
 * correct keyboard, screen reader and mobile-picker behaviour for free.
 *
 * @param {object} props
 * @param {string} [props.id]
 * @param {import('react').ReactNode} props.label
 * @param {boolean} [props.hideLabel]
 * @param {import('react').ReactNode} [props.hint]
 * @param {import('react').ReactNode} [props.error]
 * @param {boolean} [props.required]
 * @param {{value: string, label: string, disabled?: boolean}[]} [props.options] -
 *   rendered as `<option>`s. Omit and pass `<option>` children directly for
 *   grouped (`<optgroup>`) lists.
 * @param {string} [props.placeholder] - rendered as a disabled, hidden first option.
 * @param {string} [props.className] - applied to the outer `.etch-field`.
 * @param {string} [props.selectClassName] - applied to the `.etch-select`.
 */
export const Select = forwardRef(function Select(
  {
    id,
    label,
    hideLabel = false,
    hint,
    error,
    required,
    options,
    placeholder,
    className = '',
    selectClassName = '',
    children,
    ...rest
  },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`etch-field ${className}`.trim()}>
      <label htmlFor={inputId} className={`etch-field__label${hideLabel ? ' etch-visually-hidden' : ''}`}>
        {label}
        {required && (
          <span className="etch-field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <select
        ref={ref}
        id={inputId}
        className={`etch-select ${selectClassName}`.trim()}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options
          ? options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))
          : children}
      </select>
      {error ? (
        <span id={errorId} className="etch-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="etch-field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
});

export default Select;
